import { createNewDate } from '@mixins/moment-mixin.js';
import StringMixin from '@mixins/string-mixin';
import AiChatStore from '@stores/ai-chat-store';
import ArReconciliationStore from '@stores/ar-reconciliation-store';
import AuthenticationStore from '@stores/authentication-store';
import CostSheetStore from '@stores/cost-sheet-store';
import DispatchStore from '@stores/dispatch-store';
import FilterStore from '@stores/filter-store';
import JobStore from '@stores/job-store';
import LoadingStore from '@stores/loading-store';
import MembraneRequestStore from '@stores/membrane-request-store';
import ModalStore from '@stores/modal-store';
import ProposalStore from '@stores/proposal-store';
import PurchaseRequestStore from '@stores/purchase-request-store';
import ResourceStore from '@stores/resource-store';
import RouteStore from '@stores/route-store';
import ServiceScheduleStore from '@stores/service-schedule-store';
import UiStore from '@stores/ui-store';
import WaterLinxStore from '@stores/water-linx-store';
import WaterSystemStore from '@stores/water-system-store';
import { createStore } from 'vuex';
import VuexPersistence from 'vuex-persist';

import { emergencyCleanup } from '@/local-storage-cleanup';
import CustomerStore from '@/stores/customer-store';
import ProposalWizardStore from '@/stores/proposal-wizard-store';
import VendorStore from '@/stores/vendor-store';

const stores = {
    AiChatStore,
    ArReconciliationStore,
    AuthenticationStore,
    LoadingStore,
    FilterStore,
    UiStore,
    WaterLinxStore,
    WaterSystemStore,
    PurchaseRequestStore,
    ResourceStore,
    ProposalStore,
    ModalStore,
    MembraneRequestStore,
    CostSheetStore,
    JobStore,
    CustomerStore,
    ProposalWizardStore,
    ServiceScheduleStore,
    DispatchStore,
    RouteStore,
    VendorStore,
};

const dispatchFilters = new VuexPersistence({
    storage: window.localStorage,
    key: 'xlinx-dispatch-filters',
    modules: [
        'DispatchStore',
    ],
    saveState: (key, state, storage) => {
        try {
            storage.setItem(key, StringMixin.methods.compress(JSON.stringify({
                DispatchStore: {
                    date: state.ServiceScheduleStore.startDate,
                    selectedRoutes: state.ServiceScheduleStore.selectedRoutes,
                    filterByBranch: state.ServiceScheduleStore.filterByBranch,
                    filterByStatus: state.ServiceScheduleStore.filterByStatus,
                    filterByVehicle: state.ServiceScheduleStore.filterByVehicle,
                    filterByDiTechnician: state.ServiceScheduleStore.filterByDiTechnician,
                    lastOpenRouteId: state.ServiceScheduleStore.lastOpenRouteId,
                },
            })));
            // eslint-disable-next-line no-empty
        } catch (e) {
            // console.log(e);
        }
    },
    restoreState: (key, storage) => {
        try {
            const data = JSON.parse(StringMixin.methods.decompress(storage.getItem(key)));

            data.ServiceScheduleStore.date = createNewDate(data.ServiceScheduleStore.date);

            return data;
            // eslint-disable-next-line no-empty
        } catch (e) {
            // console.log(e);
        }

        return {};
    },
});

const serviceScheduleFilters = new VuexPersistence({
    storage: window.localStorage,
    key: 'xlinx-service-schedule-filters',
    modules: [
        'ServiceScheduleStore',
    ],
    saveState: (key, state, storage) => {
        try {
            storage.setItem(key, StringMixin.methods.compress(JSON.stringify({
                ServiceScheduleStore: {
                    startDate: state.ServiceScheduleStore.startDate,
                    endDate: state.ServiceScheduleStore.endDate,
                    filterByBranch: state.ServiceScheduleStore.filterByBranch,
                    lastOpenRouteId: state.ServiceScheduleStore.lastOpenRouteId,
                    selectedServiceTechnicians: state.ServiceScheduleStore.selectedServiceTechnicians,
                },
            })));
        } catch (e) {
            // eslint-disable-next-line no-console
            console.log(e);
        }
    },
    restoreState: (key, storage) => {
        try {
            const data = JSON.parse(StringMixin.methods.decompress(storage.getItem(key)));

            data.ServiceScheduleStore.startDate = createNewDate(data.ServiceScheduleStore.startDate);
            data.ServiceScheduleStore.endDate = createNewDate(data.ServiceScheduleStore.startDate);

            return data;
        } catch (e) {
            // eslint-disable-next-line no-console
            console.log(e);
        }

        return {};
    },
});

const routeStoreStorage = new VuexPersistence({
    key: 'route',
    name: 'RouteStore',
    modules: [
        'RouteStore',
    ],
    saveState: (key, state, storage) => {
        try {
            if (state.RouteStore.route.id) {
                storage.setItem(`route-${state.RouteStore.route.id}`,
                    StringMixin.methods.compress(JSON.stringify(state.RouteStore)));
            }
        } catch (e) {
            // Handle QuotaExceededError by cleaning up old entries and retrying
            if (e.name === 'QuotaExceededError' || e.code === 22) {
                emergencyCleanup();
                try {
                    if (state.RouteStore.route.id) {
                        storage.setItem(`route-${state.RouteStore.route.id}`,
                            StringMixin.methods.compress(JSON.stringify(state.RouteStore)));
                    }
                } catch (retryError) {
                    // Storage unavailable, continue without persistence
                }
            }
        }
    },
    restoreState() {},
});

const store = createStore({
    modules: stores,
    plugins: [
        dispatchFilters.plugin,
        serviceScheduleFilters.plugin,
        routeStoreStorage.plugin,
    ],
});

export default store;
